import React, { useEffect, useRef, useState } from 'react';
import '../styles/exportPreview.css';

// Draws the preview "paper" to a JPEG. The clone is un-clipped first so wide
// tables are captured whole, not just the part scrolled into view.
async function downloadPaperAsJpeg(paper, fileName) {
  const { default: html2canvas } = await import('html2canvas');
  const canvas = await html2canvas(paper, {
    scale: 2,
    backgroundColor: '#ffffff',
    onclone: (doc) => {
      const clone = doc.querySelector('.export-preview-paper');
      clone.style.overflow = 'visible';
      clone.style.maxWidth = 'none';
      clone.style.boxShadow = 'none';
      // The on-screen zoom never reaches the file: capture at 100%.
      const zoomed = clone.querySelector('.export-preview-zoom');
      if (zoomed) zoomed.style.zoom = '1';
      for (let el = clone.parentElement; el; el = el.parentElement) {
        el.style.overflow = 'visible';
        el.style.maxHeight = 'none';
      }
    },
  });
  const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', 0.95));
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `${fileName}.jpg`;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

// Zoom steps for the preview (on screen only; downloads are unaffected).
const ZOOM_MIN = 0.5;
const ZOOM_MAX = 2;
const ZOOM_STEP = 0.25;

// Shows what an export will look like before downloading it; the user then
// picks Excel, PDF or JPEG. `landscape` gives the window and its page a wide (horizontal) shape for
// wide tables. `onDownload(format)` performs the Excel / PDF
// export; the JPEG is an image of the preview itself, named `fileName`.jpg.
export default function ExportPreviewModal({
  title, fileName, loading, error, onDownload, onClose, children, landscape = false,
}) {
  const [busy, setBusy] = useState(null);
  const [failed, setFailed] = useState(false);
  const [zoom, setZoom] = useState(1);
  const paperRef = useRef(null);
  const changeZoom = (delta) => setZoom((z) => Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, z + delta)));

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const download = async (fmt) => {
    setBusy(fmt);
    setFailed(false);
    try {
      if (fmt === 'jpeg') {
        await downloadPaperAsJpeg(paperRef.current, fileName || title.replace(/[^\w-]+/g, '_'));
      } else {
        await onDownload(fmt);
      }
      onClose();
    } catch (err) {
      setFailed(true);
    } finally {
      setBusy(null);
    }
  };

  const button = (fmt, label) => (
    <button
      type="button"
      className={`export-preview-btn export-preview-btn-${fmt}`}
      onClick={() => download(fmt)}
      disabled={loading || !!error || busy !== null}
    >
      {busy === fmt ? 'Descargando…' : label}
    </button>
  );

  return (
    <div className="export-preview-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className={`export-preview-dialog${landscape ? ' export-preview-landscape' : ''}`} role="dialog" aria-modal="true" aria-label={`Vista previa: ${title}`}>
        <div className="export-preview-header">
          <div>
            <div className="export-preview-title">Vista previa</div>
            <div className="export-preview-subtitle">{title}</div>
          </div>
          <div className="export-preview-header-actions">
            <div className="export-preview-zoom-controls" role="group" aria-label="Zoom">
              <button
                type="button"
                className="export-preview-zoom-btn"
                onClick={() => changeZoom(-ZOOM_STEP)}
                disabled={zoom <= ZOOM_MIN}
                title="Alejar"
                aria-label="Alejar"
              >
                −
              </button>
              <button
                type="button"
                className="export-preview-zoom-level"
                onClick={() => setZoom(1)}
                title="Restablecer al 100%"
                aria-label={`Zoom ${Math.round(zoom * 100)}%, restablecer al 100%`}
              >
                {Math.round(zoom * 100)}%
              </button>
              <button
                type="button"
                className="export-preview-zoom-btn"
                onClick={() => changeZoom(ZOOM_STEP)}
                disabled={zoom >= ZOOM_MAX}
                title="Acercar"
                aria-label="Acercar"
              >
                +
              </button>
            </div>
            <button type="button" className="export-preview-close" onClick={onClose} aria-label="Cerrar">✖️</button>
          </div>
        </div>

        <div className="export-preview-body">
          {loading ? (
            <div className="export-preview-message">Cargando vista previa…</div>
          ) : error ? (
            <div className="export-preview-message export-preview-error">{error}</div>
          ) : (
            <div className="export-preview-paper" ref={paperRef}>
              <div className="export-preview-zoom" style={{ zoom }}>{children}</div>
            </div>
          )}
        </div>

        <div className="export-preview-footer">
          {failed ? (
            <span className="export-preview-hint export-preview-error">No se pudo descargar el archivo. Intente de nuevo.</span>
          ) : (
            <span className="export-preview-hint">El Excel, el PDF y el JPEG tienen este mismo contenido y formato.</span>
          )}
          <div className="export-preview-actions">
            <button type="button" className="export-preview-btn" onClick={onClose}>Cerrar</button>
            {button('excel', '📊 Descargar Excel')}
            {button('pdf', '📄 Descargar PDF')}
            {button('jpeg', '🖼️ Descargar JPEG')}
          </div>
        </div>
      </div>
    </div>
  );
}
